import React from 'react';
import { FormattedMessage } from 'react-intl';
import _get from 'lodash.get';

import { utilities_estimate } from '@ratehub/mortgage-calculations';
import calculatePropertyTaxAmount from './calculatePropertyTaxAmount';


function getMonthlyExpensesCalculations({
    mortgagePaymentName = 'newMortgagePayment',
    monthlyPaymentName, // No default since this is only used by Affordability
    locationName = 'location',
    homePriceName = 'homePrice',
    propertyTaxName = 'propertyTax',

    creditCardPaymentName = 'creditCardPayment',
    carPaymentName = 'carPayment',
    heatingCostsName = 'heatingCosts',
    otherExpensesName = 'otherExpenses',
}) {
    return {
        mortgagePaymentCalculation: {
            id: 'mortgagePaymentCalculation',
            name: 'mortgagePaymentCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasMonthlyExpenses.mortgagePaymentCalculationLabel"
                    defaultMessage="Mortgage payment"
                />
            ),
            operandValue: (values) => getMortgagePayment({
                monthlyPayment: _get(values, monthlyPaymentName),
                mortgagePayment: _get(values, mortgagePaymentName),
            }),
        },
        propertyTaxCalculation: {
            id: 'propertyTaxCalculation',
            name: 'propertyTaxCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasMonthlyExpenses.propertyTaxCalculationLabel"
                    defaultMessage="Property tax"
                />
            ),
            operandValue: (values) => calculatePropertyTaxAmount({
                propertyTax: _get(values, propertyTaxName),
                location: _get(values, locationName),
                homePrice: _get(values, homePriceName),
            }),
            operator: 'plus',
        },
        monthlyDebtCalculation: {
            id: 'monthlyDebtCalculation',
            name: 'monthlyDebtCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasMonthlyExpenses.monthlyDebtCalculationlabel"
                    defaultMessage="Monthly Debt Payment"
                />
            ),
            operandValue: (values) => getMonthlyDebtPayment({
                creditCardPayment: _get(values, creditCardPaymentName),
                carPayment: _get(values, carPaymentName),
                otherExpenses: _get(values, otherExpensesName),
            }),
            operator: 'plus',
        },
        utilitiesCalculation: {
            id: 'utilitiesCalculation',
            name: 'utilitiesCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasMonthlyExpenses.utilitiesCalculationLabel"
                    defaultMessage="Utilities"
                />
            ),
            operandValue: (values) => getUtilities({
                heatingCosts: _get(values, heatingCostsName),
                homePrice: _get(values, homePriceName),
            }),
            operator: 'plus',
        },
        propertyInsuranceCalculation: {
            id: 'propertyInsuranceCalculation',
            name: 'propertyInsuranceCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasMonthlyExpenses.propertyInsuranceCalculationLabel"
                    defaultMessage="Property insurance"
                />
            ),
            operandValue: () => 50,
            operator: 'plus',
        },
        phoneCalculation: {
            id: 'phoneCalculation',
            name: 'phoneCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasMonthlyExpenses.phoneCalculationLabel"
                    defaultMessage="Phone"
                />
            ),
            operandValue: () => 40,
            operator: 'plus',
        },
        cableCalculation: {
            id: 'cableCalculation',
            name: 'cableCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasMonthlyExpenses.cableCalculationLabel"
                    defaultMessage="Cable"
                />
            ),
            operandValue: () => 60,
            operator: 'plus',
        },
        internetCalculation: {
            id: 'internetCalculation',
            name: 'internetCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasMonthlyExpenses.internetCalculationLabel"
                    defaultMessage="Internet"
                />
            ),
            operandValue: () => 45,
            operator: 'plus',
        },
        monthlyExpensesCalculation: {
            id: 'monthlyExpensesCalculation',
            name: 'monthlyExpensesCalculation',
            label: (
                <FormattedMessage
                    id="mtg-calculators.FormulasMonthlyExpenses.monthlyExpensesLabel"
                    defaultMessage="Monthly expenses"
                />
            ),
            operandValue: (values) => getMonthlyExpenses({
                newMortgagePayment: _get(values, mortgagePaymentName),
                monthlyPayment: _get(values, monthlyPaymentName),
                // These values are from the earlier calculations
                propertyInsuranceCalculation: values.propertyInsuranceCalculation,
                phoneCalculation: values.phoneCalculation,
                cableCalculation: values.cableCalculation,
                internetCalculation: values.internetCalculation,
                propertyTaxCalculation: values.propertyTaxCalculation,
                utilitiesCalculation: values.utilitiesCalculation,
                monthlyDebtCalculation: values.monthlyDebtCalculation,
            }),
            operator: 'equals',
            result: true,
        },
    };
}

function getMortgagePayment({ monthlyPayment, mortgagePayment }) {
    return mortgagePayment ?? monthlyPayment ?? null;
}

function getUtilities({ heatingCosts, homePrice }) {
    return heatingCosts
        ?? (homePrice
            ? utilities_estimate(homePrice, 0)
            : null);
}

function getMonthlyDebtPayment({ creditCardPayment, carPayment, otherExpenses }) {
    return (creditCardPayment ?? 0) + (carPayment ?? 0) + (otherExpenses ?? 0);
}

function getMonthlyExpenses({
    newMortgagePayment,
    mortgagePayment,
    monthlyPayment,
    propertyInsuranceCalculation,
    phoneCalculation,
    cableCalculation,
    internetCalculation,
    propertyTaxCalculation,
    utilitiesCalculation,
    monthlyDebtCalculation,
    condoFeesCalculation,
}) {
    const valueArray = [
        newMortgagePayment ?? mortgagePayment ?? monthlyPayment,  // monthly payment, first newMortgagePayment is only used in affordability calc
        propertyInsuranceCalculation,       // property insurance
        phoneCalculation,                   // phone
        cableCalculation,                   // cable
        internetCalculation,                // internet
        propertyTaxCalculation,             // property tax
        utilitiesCalculation,               // utilities
        monthlyDebtCalculation ?? 0,        // monthly debt
        condoFeesCalculation ?? 0,          // condo fees
    ];

    if (valueArray.some(value => value == null)) {
        return null;
    }

    return valueArray.reduce((sum, value) => sum + value, 0);
}


export default getMonthlyExpensesCalculations;
export { getMonthlyExpenses };
